/**
 * Cost Ledger L-2 Group 1 — Desktop Master Ledger.
 *
 * Admin + surveyor route at /cost-ledger. Surveyor is read-only; every
 * mutating control is hidden and every mutating endpoint 403's server-side
 * so a stale UI cannot silently succeed.
 *
 *   • Filter bar — date range, project, source, status, category,
 *     supplier, tag, value_pending, include voided.
 *   • Running totals — computed on the filtered set, not the master
 *     collection. Voided rows never enter the total.
 *   • Reconciliation banner — orphan and write-failure counts wired to
 *     one-click filters. Retry button (admin only) reruns the ledger
 *     insert for a failed capture.
 *   • Table — pagination, per-row detail slideover, admin actions
 *     (Void / Edit) available in the slideover.
 *   • Export CSV — every row matching the filters (not the visible page)
 *     with values in decimal pounds.
 *   • Manual Entry dialog — admin-only, source=manual ledger row.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyLens } from '../hooks/useCompanyLens';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useVatBasis, VatBasisToggle, rowPence, totalsFor, BASIS_LABEL } from '../components/VatBasisToggle';
import { useLedgerView, LedgerViewToggle } from '../components/LedgerViewToggle';
import { LedgerTabs } from '../components/LedgerTabs';
import { LineItemsTable, toPence } from '../components/cost-inbox/LineItemsTable';
import { AuthThumb } from './diary/AuthThumb';
import { InvoiceDocViewer } from './diary/InvoiceDocViewer';
import {
  Loader2, Wallet, Filter, Download, Plus, AlertTriangle, RefreshCw,
  ChevronRight, ChevronDown, X, BanIcon, Pencil, Camera, BookOpen, Layers,
  FileText, ArrowLeft, Files, Save, EyeOff, Link2, ArrowLeftRight,
  CheckCircle2, Clock, Send, MessageSquareWarning,
} from 'lucide-react';

// ------------------------------------------------------------------
// helpers
// ------------------------------------------------------------------
const CATEGORY_LABELS = { labour: 'Labour', material: 'Material', plant: 'Plant', fuel: 'Fuel', other: 'Other' };
const CATEGORY_ORDER  = ['labour', 'material', 'plant', 'fuel', 'other'];
const TILE_ORDER      = ['labour', 'material', 'plant', 'other'];
const TILE_LABELS     = { ...CATEGORY_LABELS, plant: 'Plant & fuel' };

/** Capture v2: a provisional capture row is either still counting ("awaiting invoice") or superseded by a posted invoice. */
const CHASE_DAYS = 60;
const captureCoverState = (row) => (row?.source === 'capture' && row?.status === 'provisional' ? (row.matched ? 'covered' : 'awaiting') : null);
const captureAgeDays = (row) => (row?.created_at ? Math.floor((Date.now() - new Date(row.created_at).getTime()) / 86400000) : 0);
function CoverBadge({ row }) {
  const st = captureCoverState(row);
  if (!st) return null;
  if (st === 'covered') {
    return <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid={`cover-badge-${row.id}`}>superseded by invoice</span>;
  }
  const age = captureAgeDays(row);
  return (
    <>
      <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid={`cover-badge-${row.id}`}>awaiting invoice{row.details_only ? ' · details only' : ''}</span>
      {age >= CHASE_DAYS && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30" title="Over 60 days with no invoice — chase the supplier" data-testid={`chase-badge-${row.id}`}><AlertTriangle className="w-2.5 h-2.5" /> chase · {age}d</span>}
    </>
  );
}
/** Inter-company rebill: billed to one LUX company, cost belongs to a project of the other → recharge. */
const COMPANY_SHORT = { DEV: 'Dev', CON: 'Con' };
const COMPANY_FULL = { DEV: 'LUX Developments', CON: 'LUX Construction' };
function RebillBadge({ row }) {
  if (!row?.rebill) return null;
  if (row.rebilled) {
    return <span className="ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid={`rebill-badge-${row.id}`}>rebilled ✓</span>;
  }
  const b = COMPANY_SHORT[row.billed_to_company] || row.billed_to_company;
  const o = COMPANY_SHORT[row.company] || row.company;
  return <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30" title={`Billed to ${COMPANY_FULL[row.billed_to_company] || row.billed_to_company}, cost belongs to ${COMPANY_FULL[row.company] || row.company} — recharge`} data-testid={`rebill-badge-${row.id}`}>rebill {b}→{o}</span>;
}
/** Xero (email-to-bills) send state on a processed invoice. */
function XeroBadge({ status, id }) {
  if (!status || status === 'pending') return null;
  if (status === 'sent') {
    return <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" title="Sent to Xero" data-testid={`xero-badge-${id || 'row'}`}><CheckCircle2 className="w-2.5 h-2.5" /> Xero</span>;
  }
  return <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" title="Not sent to Xero yet" data-testid={`xero-badge-${id || 'row'}`}><AlertTriangle className="w-2.5 h-2.5" /> not to Xero</span>;
}
function QueryFlagBadge({ open, id }) {
  if (!open) return null;
  return <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" title="Flagged with an open supplier query" data-testid={`query-badge-${id || 'row'}`}><MessageSquareWarning className="w-2.5 h-2.5" /> query</span>;
}
const SOURCE_LABELS   = { capture: 'Capture', diary: 'Diary', schedule: 'Schedule', inbox: 'Inbox', manual: 'Manual', opening_balance: 'Opening balance', po: 'Purchase order' };
const STATUS_COLORS   = {
  provisional: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  confirmed:   'bg-green-500/20 text-green-300 border-green-500/30',
  void:        'bg-gray-500/20 text-gray-400 border-gray-500/30 line-through',
};

function fmtGBP(pence) {
  const v = typeof pence === 'number' ? pence : 0;
  return (v / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
}

function fmtUKDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function fmtUKDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${fmtUKDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ------------------------------------------------------------------
// page
// ------------------------------------------------------------------
export default function CostLedger() {
  const { api, user, isAdmin, isSurveyor } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // access gate — admin + surveyor only
  useEffect(() => {
    if (!user) return;
    if (!isAdmin && !isSurveyor) navigate('/portal');
  }, [user, isAdmin, isSurveyor, navigate]);

  const [projects, setProjects] = useState([]);
  const [entries,  setEntries]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [totals,   setTotals]   = useState({});
  const [basis,    setBasis]    = useVatBasis();   // ex VAT (default) | inc VAT — every figure on the page follows it
  const [view,     setView]     = useLedgerView(); // invoice (default) | line — grouping is a view concern only
  const shownTotals = totalsFor(totals, basis);
  const byCompany = totals.by_company;
  const devTotals = byCompany?.DEV ? totalsFor(byCompany.DEV, basis) : null;
  const conTotals = byCompany?.CON ? totalsFor(byCompany.CON, basis) : null;
  const [loading,  setLoading]  = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({});   // inbox_item_id -> lines shown

  // Project-scoped ("locked") ledger — entered from a project's Costs tab.
  // The project filter is fixed and there is no route to other jobs.
  const locked = searchParams.get('locked') === '1';
  const lockedProjectId = locked ? (searchParams.get('project_id') || null) : null;

  // reconciliation surfaces (both admin only)
  const [writeFailures, setWriteFailures] = useState({ total: 0, captures: [] });
  const [orphans,       setOrphans]       = useState({ total: 0, orphans: [] });
  const [reconOpen,     setReconOpen]     = useState(false);
  const [matchCount,    setMatchCount]    = useState(0);   // provisional-capture ↔ invoice proposals awaiting review
  const [rebillSummary, setRebillSummary] = useState(null);
  const [reconDismissedSig, setReconDismissedSig] = useState(() => {
    try { return localStorage.getItem('lux.ledgerReconDismissed'); } catch { return null; }
  });

  // filters
  const [projectId,     setProjectId]     = useState(searchParams.get('project_id') || 'all');
  const [source,        setSource]        = useState('all');
  const [status,        setStatus]        = useState('all');
  const [category,      setCategory]      = useState('all');
  const [supplierName,  setSupplierName]  = useState('');
  const [reference,     setReference]     = useState('');
  const [tag,           setTag]           = useState('');
  const [dateFrom,      setDateFrom]      = useState('');
  const [dateTo,        setDateTo]        = useState('');
  const [valuePending,  setValuePending]  = useState('all');   // all | true | false
  const [includeVoided, setIncludeVoided] = useState(false);
  const [search,        setSearch]        = useState('');
  const [unreconciled,  setUnreconciled]  = useState(false);   // reconcile report: invoices whose lines ≠ net
  const [rebillFilter,  setRebillFilter]  = useState(false);   // to-rebill pool: billed to one company, costed to the other
  const [rebillTab,     setRebillTab]     = useState('pending'); // pending (to rebill) | done (rebilled/archived)
  const [rebillDirection, setRebillDirection] = useState('');  // '' | dev_con | con_dev
  const [stmtOpen,      setStmtOpen]      = useState(false);   // rebill statement generator dialog
  const [xeroSelectMode, setXeroSelectMode] = useState(false); // bulk Xero marking (invoice view)
  const [selectedXeroIds, setSelectedXeroIds] = useState(() => new Set());
  const [bulkXeroBusy,  setBulkXeroBusy]  = useState(false);
  const [xeroFilter,    setXeroFilter]    = useState('all');   // all | sent | not_sent — Xero (email-to-bills) send status
  const [companyView,   setCompanyView]   = useCompanyLens();   // shared company lens (Projects/Ledger/Inbox)
  const [totalsCollapsed, setTotalsCollapsed] = useState(() => {
    try { const v = localStorage.getItem('lux.ledger.totalsCollapsed'); return v === null ? true : v === '1'; } catch { return true; }
  });
  useEffect(() => { try { localStorage.setItem('lux.ledger.totalsCollapsed', totalsCollapsed ? '1' : '0'); } catch { /* ignore */ } }, [totalsCollapsed]);

  // detail + admin dialogs
  const [detailRowId, setDetailRowId] = useState(null);
  const [detailInvoiceId, setDetailInvoiceId] = useState(null);
  const [manualOpen,  setManualOpen]  = useState(false);
  const [obOpen,      setObOpen]      = useState(false);

  // Any row that belongs to a supplier invoice opens the invoice-level
  // detail; everything else opens the single-row detail (spec: clicking any
  // invoice line opens the panel at invoice level).
  const openRow = useCallback((row) => {
    if (row.inbox_item_id) setDetailInvoiceId(row.inbox_item_id);
    else setDetailRowId(row.id);
  }, []);
  const toggleExpand = useCallback((id) => setExpanded(e => ({ ...e, [id]: !e[id] })), []);

  useEffect(() => {
    api.get('/projects?limit=200').then(r => setProjects(r.data || [])).catch(() => {});
  }, [api]);

  // Deep link from the Cost Inbox "Rebill review" button opens the rebill pool.
  useEffect(() => {
    if (searchParams.get('rebill') === '1') setRebillFilter(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildParams = useCallback((offset = 0, limit = 50) => {
    const p = new URLSearchParams();
    if (projectId !== 'all') p.set('project_id', projectId);
    if (source    !== 'all') p.set('source', source);
    if (status    !== 'all') p.set('status', status);
    if (category  !== 'all') p.set('category', category);
    if (supplierName)        p.set('supplier_name', supplierName);
    if (reference)           p.set('reference', reference);
    if (tag)                 p.set('tag', tag);
    if (dateFrom)            p.set('date_from', dateFrom);
    if (dateTo)              p.set('date_to', dateTo);
    if (valuePending === 'true')  p.set('value_pending', 'true');
    if (valuePending === 'false') p.set('value_pending', 'false');
    if (includeVoided)       p.set('include_voided', 'true');
    if (search)              p.set('search', search);
    if (unreconciled) { p.set('view', 'invoice'); p.set('unreconciled', 'true'); }
    else if (view === 'invoice') p.set('view', 'invoice');
    if (rebillFilter) {
      p.set('rebill', 'true');
      p.set('rebilled', rebillTab === 'done' ? 'true' : 'false');
      if (rebillDirection) p.set('rebill_direction', rebillDirection);
    }
    if (xeroFilter !== 'all') p.set('xero_status', xeroFilter);
    if (!locked && companyView !== 'all') p.set('company', companyView);
    p.set('limit',  String(limit));
    p.set('offset', String(offset));
    return p.toString();
  }, [projectId, source, status, category, supplierName, reference, tag,
      dateFrom, dateTo, valuePending, includeVoided, search, view, unreconciled, rebillFilter, rebillTab, rebillDirection, xeroFilter, companyView, locked]);

  const load = useCallback(async () => {
    setLoading(true);
    setExpanded({});
    try {
      const r = await api.get(`/cost-ledger/entries?${buildParams(0)}`);
      setEntries(r.data.entries || []);
      setTotal(r.data.total || 0);
      setTotals(r.data.totals || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [api, buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get(`/cost-ledger/entries?${buildParams(entries.length)}`);
      setEntries(prev => [...prev, ...(r.data.entries || [])]);
    } catch { toast.error('Failed to load more'); }
    finally { setLoadingMore(false); }
  };

  const loadReconciliation = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [wf, or, ms, rb] = await Promise.all([
        api.get('/cost-ledger/reconciliation/write-failures?limit=100'),
        api.get('/cost-ledger/reconciliation/orphans?limit=100'),
        api.get('/cost-ledger/match-queue/summary'),
        api.get('/cost-ledger/rebill/summary'),
      ]);
      setWriteFailures(wf.data || { total: 0, captures: [] });
      setOrphans(or.data || { total: 0, orphans: [] });
      setMatchCount(ms.data?.total || 0);
      setRebillSummary(rb.data || null);
    } catch { /* silent */ }
  }, [api, isAdmin]);

  useEffect(() => { if (isAdmin || isSurveyor) load(); }, [isAdmin, isSurveyor, load]);
  useEffect(() => { loadReconciliation(); }, [loadReconciliation]);

  const clearFilters = () => {
    if (!locked) { setProjectId('all'); setSearchParams({}); }
    setSource('all'); setStatus('all'); setCategory('all');
    setSupplierName(''); setReference(''); setTag(''); setDateFrom(''); setDateTo('');
    setValuePending('all'); setIncludeVoided(false); setSearch(''); setUnreconciled(false); setRebillFilter(false); setRebillTab('pending'); setRebillDirection(''); setXeroSelectMode(false); setSelectedXeroIds(new Set()); setXeroFilter('all');
    if (locked) setSearchParams({ project_id: lockedProjectId, locked: '1' });
  };
  const hasFilters = (!locked && projectId !== 'all') || source !== 'all' || status !== 'all'
    || category !== 'all' || supplierName || reference || tag || dateFrom
    || dateTo || valuePending !== 'all' || includeVoided || search || unreconciled || rebillFilter || xeroFilter !== 'all';

  const lockedProject = lockedProjectId ? projects.find(p => p.id === lockedProjectId) : null;
  const lockedProjectRef = lockedProject?.project_ref;
  const lockedProjectName = lockedProject?.client_name || lockedProject?.address_line1;

  // Reconciliation notice can be hidden once the user has seen it; it re-appears
  // if the counts change (a new write-failure / orphan). Kept per browser.
  const reconSig = `${writeFailures.total}|${orphans.total}`;
  const reconVisible = reconDismissedSig !== reconSig;
  const hideRecon = () => { setReconDismissedSig(reconSig); try { localStorage.setItem('lux.ledgerReconDismissed', reconSig); } catch { /* ignore */ } };
  const showRecon = () => { setReconDismissedSig(null); try { localStorage.removeItem('lux.ledgerReconDismissed'); } catch { /* ignore */ } };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (projectId !== 'all') p.set('project_id', projectId);
      if (source    !== 'all') p.set('source', source);
      if (status    !== 'all') p.set('status', status);
      if (category  !== 'all') p.set('category', category);
      if (supplierName)        p.set('supplier_name', supplierName);
      if (reference)           p.set('reference', reference);
      if (tag)                 p.set('tag', tag);
      if (dateFrom)            p.set('date_from', dateFrom);
      if (dateTo)              p.set('date_to', dateTo);
      if (valuePending === 'true')  p.set('value_pending', 'true');
      if (valuePending === 'false') p.set('value_pending', 'false');
      if (includeVoided)       p.set('include_voided', 'true');
      if (search)              p.set('search', search);
      if (rebillFilter) {
        p.set('rebill', 'true');
        p.set('rebilled', rebillTab === 'done' ? 'true' : 'false');
        if (rebillDirection) p.set('rebill_direction', rebillDirection);
      }
      if (xeroFilter !== 'all') p.set('xero_status', xeroFilter);
      if (!locked && companyView !== 'all') p.set('company', companyView);

      const res = await api.get(`/cost-ledger/entries.csv?${p.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const disp = res.headers?.['content-disposition'] || '';
      const m = disp.match(/filename\s*=\s*"?([^";]+)"?/i);
      a.download = m ? m[1] : `cost-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const applyFilterFor = (patch) => {
    // Programmatic filter setter — used by reconciliation quick-links.
    if ('project_id' in patch)     setProjectId(patch.project_id);
    if ('source' in patch)         setSource(patch.source);
    if ('status' in patch)         setStatus(patch.status);
    if ('include_voided' in patch) setIncludeVoided(patch.include_voided);
  };

  const [markingRebill, setMarkingRebill] = useState(false);
  const shownRebillRowIds = entries.flatMap(e => e.group_type === 'invoice'
    ? (e.lines || []).filter(l => l.rebill && !l.rebilled).map(l => l.id)
    : (e.rebill && !e.rebilled ? [e.id] : []));
  const markShownRebilled = async () => {
    if (!shownRebillRowIds.length) return;
    setMarkingRebill(true);
    try {
      const r = await api.post('/cost-ledger/rebill/mark', { row_ids: shownRebillRowIds, rebilled: true });
      toast.success(`Marked ${r.data?.updated || shownRebillRowIds.length} line${shownRebillRowIds.length === 1 ? '' : 's'} rebilled`);
      load(); loadReconciliation();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not mark rebilled'); }
    finally { setMarkingRebill(false); }
  };

  const projectRefLabel = (row) => row.project_ref || (row.project_id ? row.project_id.slice(0, 8) : 'Overhead');

  // Bulk Xero marking (invoice view) — tick invoices then mark them sent / not sent.
  const toggleXeroSelect = useCallback((inboxItemId) => {
    setSelectedXeroIds(prev => {
      const next = new Set(prev);
      if (next.has(inboxItemId)) next.delete(inboxItemId); else next.add(inboxItemId);
      return next;
    });
  }, []);
  const selectableIds = entries.filter(e => e.group_type === 'invoice' && e.inbox_item_id).map(e => e.inbox_item_id);
  const allShownSelected = selectableIds.length > 0 && selectableIds.every(id => selectedXeroIds.has(id));
  const toggleSelectAllShown = () => {
    setSelectedXeroIds(prev => {
      if (allShownSelected) { const n = new Set(prev); selectableIds.forEach(id => n.delete(id)); return n; }
      return new Set([...prev, ...selectableIds]);
    });
  };
  const bulkMarkXero = async (xeroStatus) => {
    const ids = [...selectedXeroIds];
    if (!ids.length) return;
    setBulkXeroBusy(true);
    try {
      const r = await api.post('/cost-inbox/xero-status/bulk', { item_ids: ids, status: xeroStatus });
      const n = r.data?.updated || 0;
      toast.success(`Marked ${n} invoice${n === 1 ? '' : 's'} ${xeroStatus === 'sent' ? 'sent to Xero' : 'not sent'}${r.data?.skipped ? ` · ${r.data.skipped} skipped` : ''}`);
      setSelectedXeroIds(new Set());
      load(); loadReconciliation();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk update failed'); }
    finally { setBulkXeroBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#1E2530] text-white p-4 md:p-6" data-testid="cost-ledger-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
              <Wallet className="w-6 h-6 text-[#F5A623]" /> Cost Ledger
              {locked && (
                <span className="text-base font-semibold px-3 py-1.5 rounded-md bg-[#F5A623]/15 text-[#F5A623] border border-[#F5A623]/40" data-testid="ledger-title-project">
                  {lockedProjectRef || 'Project'}
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-400" data-testid="cost-ledger-total">
              {locked && lockedProjectName && <span className="text-gray-300">{lockedProjectName} · </span>}
              {loading ? 'Loading…' : view === 'invoice'
                ? `${total.toLocaleString()} ${total === 1 ? 'entry' : 'entries'} · showing ${entries.length}`
                : `${total.toLocaleString()} row${total === 1 ? '' : 's'} · showing ${entries.length}`}
              {isSurveyor && !isAdmin && <span className="ml-2 text-xs bg-white/10 px-2 py-0.5 rounded">Read-only</span>}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <Button
                onClick={() => navigate('/cost-ledger/matches')}
                variant="outline"
                className="relative border-white/20 text-white bg-white/5 hover:bg-white/10"
                data-testid="cost-ledger-match-review-btn"
              >
                <Link2 className="w-4 h-4 mr-2 text-[#F5A623]" /> Match review
                {matchCount > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold rounded-full bg-[#F5A623] text-[#1E2530] px-1" data-testid="cost-ledger-match-count">{matchCount}</span>}
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => { setRebillFilter(true); setView('invoice'); setRebillTab('pending'); }}
                variant="outline"
                className={`relative border-white/20 text-white hover:bg-white/10 ${rebillFilter ? 'bg-purple-500/25 border-purple-500/50' : 'bg-white/5'}`}
                data-testid="cost-ledger-rebill-review-btn"
              >
                <ArrowLeftRight className="w-4 h-4 mr-2 text-purple-300" /> Re-bill
                {rebillSummary?.invoice_count > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold rounded-full bg-purple-500 text-white px-1" data-testid="cost-ledger-rebill-count">{rebillSummary.invoice_count}</span>}
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => setObOpen(true)}
                variant="outline"
                className="border-white/20 text-white bg-white/5 hover:bg-white/10"
                data-testid="cost-ledger-opening-balances-btn"
              >
                <Layers className="w-4 h-4 mr-2" /> Opening balances
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => setManualOpen(true)}
                variant="outline"
                className="border-white/20 text-white bg-white/5 hover:bg-white/10"
                data-testid="cost-ledger-manual-entry-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Manual entry
              </Button>
            )}
            <Button
              onClick={exportCsv}
              disabled={exporting || loading}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
              data-testid="cost-ledger-export-csv-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
          </div>
        </div>

        {/* Ledger tabs — Cost ledger · Client POs (global ledger only) */}
        {!locked && <LedgerTabs className="mb-4" />}

        {/* Company lens — Global vs each trading company (global ledger only) */}
        {!locked && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="ledger-company-view">
            <span className="text-xs uppercase tracking-wider text-gray-500 mr-1">Viewing</span>
            {[
              { key: 'all', label: 'Global', hint: 'Every job across both companies', active: 'bg-[#F5A623] text-[#1E2530] border-[#F5A623]' },
              { key: 'DEV', label: 'LUX Developments', hint: 'LUX Developments jobs only', active: 'bg-[#F5A623] text-[#1E2530] border-[#F5A623]' },
              { key: 'CON', label: 'LUX Construction', hint: 'LUX Construction jobs only', active: 'bg-sky-500 text-white border-sky-500' },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setCompanyView(opt.key)}
                title={opt.hint}
                className={`h-9 px-4 rounded-md text-sm border transition-colors ${companyView === opt.key ? `${opt.active} font-semibold` : 'bg-[#2A3240] text-gray-300 border-white/15 hover:bg-white/5'}`}
                data-testid={`ledger-company-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Notices — project scope + reconciliation together, each minimisable */}
        {(locked || (isAdmin && (writeFailures.total > 0 || orphans.total > 0))) && (
          <div className="mb-4 rounded-lg border border-[#F5A623]/40 bg-[#F5A623]/10 divide-y divide-[#F5A623]/20 overflow-hidden" data-testid="ledger-notices">
            {locked && (
              <div className="flex items-center justify-between gap-3 px-4 py-3" data-testid="ledger-locked-banner">
                <div className="text-sm text-[#F5D9A0] flex items-center gap-2 min-w-0">
                  <Wallet className="w-4 h-4 shrink-0 text-[#F5A623]" />
                  <span className="truncate">
                    Costs for <span className="font-semibold text-white" data-testid="ledger-locked-project">{lockedProjectRef || 'this project'}</span>{lockedProjectName ? ` — ${lockedProjectName}` : ''} only. The full ledger across every job is in the top navigation.
                  </span>
                </div>
                <Button
                  onClick={() => navigate(`/projects/${lockedProjectId}?tab=costs`)}
                  variant="outline"
                  className="border-white/20 text-white bg-white/5 hover:bg-white/10 shrink-0"
                  data-testid="ledger-back-to-project"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to project
                </Button>
              </div>
            )}
            {isAdmin && (writeFailures.total > 0 || orphans.total > 0) && (reconVisible ? (
              <div className="text-amber-200" data-testid="cost-ledger-reconciliation-banner">
                <div className="flex items-center gap-2 px-4 py-3">
                  <button
                    className="flex-1 flex items-center justify-between text-left"
                    onClick={() => setReconOpen(v => !v)}
                    data-testid="cost-ledger-reconciliation-toggle"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium flex-wrap">
                      <AlertTriangle className="w-4 h-4" />
                      Reconciliation surfaces
                      <span className="text-xs bg-amber-500/25 rounded-full px-2 py-0.5" data-testid="cost-ledger-writefailure-count">
                        {writeFailures.total} write-failure{writeFailures.total === 1 ? '' : 's'}
                      </span>
                      <span className="text-xs bg-amber-500/25 rounded-full px-2 py-0.5" data-testid="cost-ledger-orphan-count">
                        {orphans.total} orphan{orphans.total === 1 ? '' : 's'}
                      </span>
                    </div>
                    <ChevronRight className={`w-4 h-4 transition-transform ${reconOpen ? 'rotate-90' : ''}`} />
                  </button>
                  <button type="button" onClick={hideRecon} className="shrink-0 inline-flex items-center gap-1 text-xs text-amber-300/80 hover:text-amber-100" title="Hide this notice until the counts change" data-testid="cost-ledger-reconciliation-hide">
                    <EyeOff className="w-3.5 h-3.5" /> Hide
                  </button>
                </div>
                {reconOpen && (
                  <div className="px-4 pb-3 space-y-3">
                    {writeFailures.captures.length > 0 && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">Write failures</div>
                        <div className="space-y-2">
                          {writeFailures.captures.slice(0, 5).map(c => (
                            <div key={c.id} className="text-xs bg-white/5 rounded p-2 flex items-center justify-between gap-3"
                                 data-testid={`writefailure-row-${c.id}`}>
                              <div className="min-w-0">
                                <div className="text-white truncate">
                                  <Camera className="inline w-3 h-3 mr-1 text-amber-300" />
                                  {c.user_name || c.submitted_by_name || 'Unknown'} · {c.linked_project_ref || 'Overhead'} · {c.ledger_write_attempted_category || 'unknown'}
                                </div>
                                <div className="text-amber-300/70 truncate">{c.ledger_write_error || 'No error text'}</div>
                              </div>
                              <RetryButton api={api} captureId={c.id} onSuccess={() => { loadReconciliation(); load(); }} />
                            </div>
                          ))}
                          {writeFailures.total > 5 && (
                            <div className="text-xs text-amber-300/70">…and {writeFailures.total - 5} more</div>
                          )}
                        </div>
                      </div>
                    )}
                    {orphans.orphans.length > 0 && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">Orphan ledger rows</div>
                        <div className="space-y-1">
                          {orphans.orphans.slice(0, 5).map(o => (
                            <button
                              key={o.id}
                              className="w-full text-left text-xs bg-white/5 rounded p-2 hover:bg-white/10"
                              onClick={() => setDetailRowId(o.id)}
                              data-testid={`orphan-row-${o.id}`}
                            >
                              <div className="text-white truncate">
                                {o.category} · {o.reference || o.id.slice(0, 8)} · <span className="text-amber-300/80">{o.orphan_reason.replace('_', ' ')}</span>
                              </div>
                            </button>
                          ))}
                          {orphans.total > 5 && (
                            <div className="text-xs text-amber-300/70">…and {orphans.total - 5} more</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="px-4 py-2 flex items-center justify-between gap-3 text-xs text-amber-300/70" data-testid="cost-ledger-reconciliation-hidden">
                <span className="inline-flex items-center gap-1.5 min-w-0"><EyeOff className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">Reconciliation notice hidden · {writeFailures.total} write-failures · {orphans.total} orphans</span></span>
                <button type="button" onClick={showRecon} className="underline hover:text-amber-100 shrink-0" data-testid="cost-ledger-reconciliation-show">Show</button>
              </div>
            ))}
          </div>
        )}

        {/* Running totals — filter-scoped, ex VAT by default */}
        <div className="flex items-center justify-between gap-3 mb-2" data-testid="cost-ledger-totals-header">
          <button type="button" onClick={() => setTotalsCollapsed(v => !v)} className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-gray-400 hover:text-gray-200 transition-colors" data-testid="cost-ledger-totals-toggle">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${totalsCollapsed ? '-rotate-90' : ''}`} />
            Running totals · {!locked && companyView !== 'all' && <span className="text-white/80" data-testid="cost-ledger-company-label">{companyView === 'DEV' ? 'LUX Developments' : 'LUX Construction'} · </span>}<span className="text-white/80" data-testid="cost-ledger-basis-label">{BASIS_LABEL[basis]}</span>
          </button>
          <VatBasisToggle basis={basis} onChange={setBasis} testid="cost-ledger-vat-toggle" />
        </div>

        {!totalsCollapsed && (<>
        {/* Company split strip — Developments vs Construction at a glance (Global view only) */}
        {!locked && companyView === 'all' && byCompany && devTotals && conTotals && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="cost-ledger-company-strip">
            <button type="button" onClick={() => setCompanyView('DEV')} className="text-left bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-lg p-3 hover:bg-[#F5A623]/20 transition-colors" data-testid="company-strip-DEV">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-[#F5A623] font-semibold">LUX Developments</span>
                <span className="text-[10px] text-gray-400">View →</span>
              </div>
              <div className="text-2xl font-bold text-white mt-1" data-testid="company-strip-DEV-total">{fmtGBP(devTotals.headline_pence || 0)}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">{BASIS_LABEL[basis]} · Labour {fmtGBP(devTotals.labour_pence || 0)} · Materials {fmtGBP(devTotals.material_pence || 0)} · Plant {fmtGBP(devTotals.plant_pence || 0)} · Other {fmtGBP(devTotals.other_pence || 0)}</div>
            </button>
            <button type="button" onClick={() => setCompanyView('CON')} className="text-left bg-sky-500/10 border border-sky-500/30 rounded-lg p-3 hover:bg-sky-500/20 transition-colors" data-testid="company-strip-CON">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-sky-300 font-semibold">LUX Construction</span>
                <span className="text-[10px] text-gray-400">View →</span>
              </div>
              <div className="text-2xl font-bold text-white mt-1" data-testid="company-strip-CON-total">{fmtGBP(conTotals.headline_pence || 0)}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">{BASIS_LABEL[basis]} · Labour {fmtGBP(conTotals.labour_pence || 0)} · Materials {fmtGBP(conTotals.material_pence || 0)} · Plant {fmtGBP(conTotals.plant_pence || 0)} · Other {fmtGBP(conTotals.other_pence || 0)}</div>
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="cost-ledger-totals">
          {TILE_ORDER.map(cat => (
            <div key={cat}
                 className="bg-[#2A3240] rounded-lg p-3 border border-white/10"
                 data-testid={`cost-ledger-total-${cat}`}>
              <div className="text-[11px] uppercase tracking-wider text-gray-400">{TILE_LABELS[cat]}</div>
              <div className="text-2xl font-bold text-white mt-1">
                {fmtGBP(shownTotals[`${cat}_pence`] || 0)}
              </div>
            </div>
          ))}
        </div>
        <div className="mb-4 text-xs text-gray-400 -mt-1">
          Totals reflect the current filter set (Labour + Material + Plant & fuel + Other in headline). Labour here is ledger rows only — diary day-rate labour is added on the project pages.
          <span className="ml-2 text-white/70" data-testid="cost-ledger-headline-total">
            Headline: {fmtGBP(shownTotals.headline_pence || 0)} {BASIS_LABEL[basis]}
          </span>
          {(totals.vat_pence || 0) > 0 && <span className="ml-2 text-gray-500" data-testid="cost-ledger-vat-total">· VAT {fmtGBP(totals.vat_pence)}</span>}
        </div>
        </>)}

        {/* Filter bar */}
        <div className="bg-[#2A3240] rounded-lg p-3 mb-4 space-y-2 border border-white/10" data-testid="cost-ledger-filters">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <LedgerViewToggle view={unreconciled ? 'invoice' : view} onChange={(v) => { setView(v); if (v === 'line') setUnreconciled(false); }} testid="ledger-view-toggle" />
            {locked ? (
              <div className="w-[220px] h-9 flex items-center gap-2 px-3 rounded-md bg-[#1E2530] border border-white/10 text-xs text-gray-300" data-testid="filter-project-locked" title="Locked to this project">
                <Wallet className="w-3.5 h-3.5 text-[#F5A623] shrink-0" />
                <span className="truncate">{lockedProjectRef || 'This project'}</span>
              </div>
            ) : (
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="w-[220px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-project">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
                <SelectItem value="all" className="text-white">All projects</SelectItem>
                <SelectItem value="null" className="text-white">Overheads only (no project)</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-white">
                    {p.project_ref} — {p.client_name || p.address_line1 || ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}

            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[140px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all" className="text-white">All categories</SelectItem>
                {CATEGORY_ORDER.map(c => <SelectItem key={c} value={c} className="text-white">{CATEGORY_LABELS[c]}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-[140px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-source">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all" className="text-white">All sources</SelectItem>
                {Object.keys(SOURCE_LABELS).map(s => <SelectItem key={s} value={s} className="text-white">{SOURCE_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[140px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all" className="text-white">All statuses</SelectItem>
                <SelectItem value="provisional" className="text-white">Provisional</SelectItem>
                <SelectItem value="confirmed"   className="text-white">Confirmed</SelectItem>
                <SelectItem value="void"        className="text-white">Voided only</SelectItem>
              </SelectContent>
            </Select>

            <Select value={valuePending} onValueChange={setValuePending}>
              <SelectTrigger className="w-[150px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-value-pending">
                <SelectValue placeholder="Value" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                <SelectItem value="all"   className="text-white">Any value</SelectItem>
                <SelectItem value="true"  className="text-white">Value pending</SelectItem>
                <SelectItem value="false" className="text-white">Has a value</SelectItem>
              </SelectContent>
            </Select>

            <label className="flex items-center gap-2 text-xs text-gray-300 ml-1">
              <input
                type="checkbox"
                checked={includeVoided}
                onChange={e => setIncludeVoided(e.target.checked)}
                data-testid="filter-include-voided"
                className="accent-[#F5A623]"
              />
              Include voided
            </label>

            <button
              type="button"
              onClick={() => { const nv = !unreconciled; setUnreconciled(nv); if (nv) { setView('invoice'); setCategory('all'); } }}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border transition-colors ml-1 ${unreconciled ? 'bg-amber-500/20 text-amber-200 border-amber-500/40 font-medium' : 'bg-[#1E2530] text-gray-300 border-white/20 hover:bg-white/5'}`}
              title="Show only supplier invoices whose lines don't add up to the invoice net"
              data-testid="filter-unreconciled"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Doesn't reconcile
            </button>

            <button
              type="button"
              onClick={() => setRebillFilter(v => !v)}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border transition-colors ${rebillFilter ? 'bg-purple-500/20 text-purple-200 border-purple-500/40 font-medium' : 'bg-[#1E2530] text-gray-300 border-white/20 hover:bg-white/5'}`}
              title="Show only inter-company rebill lines — billed to one company, costed to the other, still to recharge"
              data-testid="filter-rebill"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" /> To rebill
              {rebillSummary?.invoice_count > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 text-[10px] font-semibold rounded-full bg-purple-500/40 text-white px-1">{rebillSummary.invoice_count}</span>}
            </button>

            <button
              type="button"
              onClick={() => setXeroFilter(v => v === 'sent' ? 'all' : 'sent')}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border transition-colors ${xeroFilter === 'sent' ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40 font-medium' : 'bg-[#1E2530] text-gray-300 border-white/20 hover:bg-white/5'}`}
              title="Show only invoices already sent to Xero"
              data-testid="filter-xero-sent"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Sent to Xero
            </button>
            <button
              type="button"
              onClick={() => setXeroFilter(v => v === 'not_sent' ? 'all' : 'not_sent')}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border transition-colors ${xeroFilter === 'not_sent' ? 'bg-amber-500/20 text-amber-200 border-amber-500/40 font-medium' : 'bg-[#1E2530] text-gray-300 border-white/20 hover:bg-white/5'}`}
              title="The gap-catcher — approved invoices not yet sent to Xero (includes failed)"
              data-testid="filter-xero-not-sent"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Not sent to Xero
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => { const nv = !xeroSelectMode; setXeroSelectMode(nv); if (nv) setView('invoice'); else setSelectedXeroIds(new Set()); }}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border transition-colors ${xeroSelectMode ? 'bg-sky-500/20 text-sky-200 border-sky-500/40 font-medium' : 'bg-[#1E2530] text-gray-300 border-white/20 hover:bg-white/5'}`}
                title="Tick invoices to mark them sent / not sent to Xero in bulk — useful during setup (invoice view)"
                data-testid="xero-bulk-select-toggle"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> {xeroSelectMode ? 'Selecting…' : 'Bulk Xero mark'}
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reference / supplier / tag"
                   className="w-[280px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-search" />
            <Input value={supplierName} onChange={e => setSupplierName(e.target.value)} placeholder="Supplier name"
                   className="w-[180px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-supplier-name" />
            <Input value={tag} onChange={e => setTag(e.target.value)} placeholder="Tag"
                   className="w-[130px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-tag" />
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                   className="w-[150px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-date-from" />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                   className="w-[150px] bg-[#1E2530] border-white/20 text-white text-xs h-9" data-testid="filter-date-to" />
            <Button onClick={load} disabled={loading} size="sm"
                    className="bg-white/10 hover:bg-white/20 text-white h-9"
                    data-testid="filter-apply-btn">
              Apply
            </Button>
            {hasFilters && (
              <Button onClick={clearFilters} variant="ghost" size="sm"
                      className="text-gray-300 hover:text-white h-9"
                      data-testid="filter-clear-btn">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        {/* Rebill pool — tabs (To rebill / Rebilled), direction filter, statement + summary */}
        {rebillFilter && rebillSummary && (() => {
          const active = rebillTab === 'done' ? (rebillSummary.done || {}) : rebillSummary;
          return (
          <div className="mb-4 rounded-lg border border-purple-500/30 bg-purple-500/[0.08] p-3 space-y-3" data-testid="rebill-pool-strip">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="inline-flex rounded-md border border-purple-500/30 overflow-hidden" data-testid="rebill-tabs">
                <button type="button" onClick={() => setRebillTab('pending')} className={`px-3 h-8 text-sm ${rebillTab === 'pending' ? 'bg-purple-500 text-white font-semibold' : 'bg-transparent text-purple-200 hover:bg-purple-500/15'}`} data-testid="rebill-tab-pending">
                  To rebill{rebillSummary.invoice_count > 0 ? ` (${rebillSummary.invoice_count})` : ''}
                </button>
                <button type="button" onClick={() => setRebillTab('done')} className={`px-3 h-8 text-sm border-l border-purple-500/30 ${rebillTab === 'done' ? 'bg-purple-500 text-white font-semibold' : 'bg-transparent text-purple-200 hover:bg-purple-500/15'}`} data-testid="rebill-tab-done">
                  Rebilled{rebillSummary.done?.invoice_count > 0 ? ` (${rebillSummary.done.invoice_count})` : ''}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setStmtOpen(true)} className="h-8 border-purple-500/40 text-purple-100 bg-purple-500/10 hover:bg-purple-500/20" data-testid="rebill-statement-btn">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Generate statement
                </Button>
                {isAdmin && rebillTab === 'pending' && shownRebillRowIds.length > 0 && (
                  <Button size="sm" onClick={markShownRebilled} disabled={markingRebill} className="bg-purple-500 hover:bg-purple-400 text-white font-semibold shrink-0" data-testid="rebill-mark-shown-btn">
                    {markingRebill ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null} Mark shown rebilled ({shownRebillRowIds.length})
                  </Button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-testid="rebill-direction-chips">
              <span className="text-xs uppercase tracking-wider text-purple-300/70 mr-1">Direction</span>
              {[
                { key: '', label: 'All directions' },
                { key: 'dev_con', label: 'Developments → Construction' },
                { key: 'con_dev', label: 'Construction → Developments' },
              ].map(opt => (
                <button key={opt.key || 'all'} type="button" onClick={() => setRebillDirection(opt.key)}
                  className={`h-7 px-2.5 rounded-md text-xs border transition-colors ${rebillDirection === opt.key ? 'bg-purple-500 text-white border-purple-500 font-semibold' : 'bg-transparent text-purple-200 border-purple-500/30 hover:bg-purple-500/15'}`}
                  data-testid={`rebill-direction-${opt.key || 'all'}`}>
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="text-sm text-purple-100">
              {rebillTab === 'done' ? (
                <>
                  <span className="font-semibold">Rebilled / archived</span> — recharges already actioned.
                  <span className="ml-2 text-purple-200">LUX Developments recharged {fmtGBP(active.dev_paid_con_pence || 0)} to Construction</span>
                  <span className="mx-1 text-purple-400">·</span>
                  <span className="text-purple-200">LUX Construction recharged {fmtGBP(active.con_paid_dev_pence || 0)} to Developments</span>
                </>
              ) : (
                <>
                  <span className="font-semibold">To rebill</span> — inter-company recharges still owed.
                  <span className="ml-2 text-purple-200" data-testid="rebill-dev-paid-con">LUX Developments paid {fmtGBP(active.dev_paid_con_pence || 0)} of Construction costs</span>
                  <span className="mx-1 text-purple-400">·</span>
                  <span className="text-purple-200" data-testid="rebill-con-paid-dev">LUX Construction paid {fmtGBP(active.con_paid_dev_pence || 0)} of Developments costs</span>
                </>
              )}
              <span className="ml-2 text-purple-300/80 text-xs">({active.invoice_count || 0} invoice{(active.invoice_count || 0) === 1 ? '' : 's'} · {active.count || 0} line{(active.count || 0) === 1 ? '' : 's'} · Export CSV downloads this list)</span>
            </div>
          </div>
          );
        })()}

        {/* Table */}
        <div className="bg-[#2A3240] rounded-lg overflow-hidden border border-white/10" data-testid="cost-ledger-table">
          <div className="grid grid-cols-[110px_170px_90px_100px_1fr_120px_110px_100px_40px] gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-gray-400 border-b border-white/10">
            <div>Date</div>
            <div>Project</div>
            <div>Source</div>
            <div>Category</div>
            <div>Supplier / Reference / Tags</div>
            <div className="text-right">Value <span className="text-gray-600 normal-case tracking-normal" data-testid="cost-ledger-column-basis">{BASIS_LABEL[basis]}</span></div>
            <div>Status</div>
            <div>By</div>
            <div />
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading ledger…
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center text-gray-400" data-testid="cost-ledger-empty">
              {unreconciled ? 'Every invoice reconciles — no mismatches to report. ✓' : 'No rows match the current filters.'}
            </div>
          ) : (
            <div>
              {entries.map(item => (
                item.group_type === 'invoice'
                  ? (
                    <InvoiceGroupRow
                      key={item.id}
                      group={item}
                      basis={basis}
                      expanded={!!expanded[item.id]}
                      onToggle={() => toggleExpand(item.id)}
                      onOpen={() => setDetailInvoiceId(item.inbox_item_id)}
                      projectRefLabel={projectRefLabel}
                      selectMode={xeroSelectMode}
                      selected={selectedXeroIds.has(item.inbox_item_id)}
                      onToggleSelect={() => toggleXeroSelect(item.inbox_item_id)}
                    />
                  ) : (
                    <SingleLedgerRow
                      key={item.id}
                      row={item}
                      basis={basis}
                      onOpen={() => openRow(item)}
                      projectRefLabel={projectRefLabel}
                    />
                  )
              ))}
              {entries.length < total && (
                <div className="p-3 text-center">
                  <Button onClick={loadMore} disabled={loadingMore} variant="ghost"
                          className="text-gray-300 hover:text-white"
                          data-testid="cost-ledger-load-more-btn">
                    {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Load more ({total - entries.length} remaining)
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Row detail slideover */}
      {detailRowId && (
        <LedgerRowDetail
          api={api}
          isAdmin={isAdmin}
          rowId={detailRowId}
          onClose={() => setDetailRowId(null)}
          onChanged={() => { setDetailRowId(null); load(); loadReconciliation(); }}
        />
      )}

      {/* Invoice-level detail slideover (any row from the Cost Inbox) */}
      {detailInvoiceId && (
        <InvoiceDetail
          api={api}
          isAdmin={isAdmin}
          inboxItemId={detailInvoiceId}
          projectId={locked ? lockedProjectId : null}
          projects={projects}
          onClose={() => setDetailInvoiceId(null)}
          onChanged={() => { load(); loadReconciliation(); }}
        />
      )}

      {/* Manual entry dialog */}
      {isAdmin && manualOpen && (
        <ManualEntryDialog
          api={api}
          projects={projects}
          onClose={() => setManualOpen(false)}
          onCreated={() => { setManualOpen(false); load(); }}
        />
      )}

      {/* Opening balances dialog */}
      {isAdmin && obOpen && (
        <OpeningBalancesDialog
          api={api}
          projects={projects}
          onClose={() => setObOpen(false)}
          onChanged={() => { load(); loadReconciliation(); }}
        />
      )}

      {/* Rebill statement generator */}
      {stmtOpen && (
        <RebillStatementDialog api={api} onClose={() => setStmtOpen(false)} />
      )}

      {/* Bulk Xero marking bar (invoice view) */}
      {xeroSelectMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#2A3240] border border-sky-500/40 shadow-xl shadow-black/40" data-testid="xero-bulk-bar">
          <span className="text-sm text-white font-medium" data-testid="xero-bulk-count">{selectedXeroIds.size} selected</span>
          <button type="button" onClick={toggleSelectAllShown} className="text-xs text-sky-300 hover:underline" data-testid="xero-bulk-select-all">{allShownSelected ? 'Clear shown' : 'Select all shown'}</button>
          <span className="w-px h-5 bg-white/15" />
          <Button size="sm" onClick={() => bulkMarkXero('sent')} disabled={bulkXeroBusy || selectedXeroIds.size === 0} className="h-8 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold" data-testid="xero-bulk-mark-sent">
            {bulkXeroBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />} Mark sent
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulkMarkXero('not_sent')} disabled={bulkXeroBusy || selectedXeroIds.size === 0} className="h-8 border-amber-500/40 text-amber-100 bg-amber-500/10 hover:bg-amber-500/20" data-testid="xero-bulk-mark-not-sent">
            {bulkXeroBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />} Mark not sent
          </Button>
          <button type="button" onClick={() => { setXeroSelectMode(false); setSelectedXeroIds(new Set()); }} className="text-gray-400 hover:text-white" title="Done" data-testid="xero-bulk-done"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Rebill statement generator — pick month + direction, download PDF / CSV
// ------------------------------------------------------------------
function RebillStatementDialog({ api, onClose }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [direction, setDirection] = useState('dev_con');
  const [busy, setBusy] = useState('');
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await api.get(`/cost-ledger/rebill/statement.${fmt}?month=${month}&direction=${direction}`, { responseType: 'blob' });
      const mime = fmt === 'pdf' ? 'application/pdf' : 'text/csv';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rebill-statement-${direction}-${month}.${fmt}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Statement ${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate statement');
    } finally { setBusy(''); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#1E2530] border-white/10 text-white" data-testid="rebill-statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#F5A623]" /> Rebill statement</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-gray-400">Pick a month and direction, then download the inter-company recharge statement. It lists every rebill line billed by one company for the other's jobs in that month.</p>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-400">Month</label>
            <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="mt-1 bg-[#2A3240] border-white/20 text-white" data-testid="rebill-statement-month" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-400">Direction</label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="mt-1 bg-[#2A3240] border-white/20 text-white" data-testid="rebill-statement-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10 text-white">
                <SelectItem value="dev_con" className="text-white">LUX Developments → recharge to Construction</SelectItem>
                <SelectItem value="con_dev" className="text-white">LUX Construction → recharge to Developments</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => download('csv')} disabled={!!busy} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="rebill-statement-csv-btn">
            {busy === 'csv' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Download CSV
          </Button>
          <Button onClick={() => download('pdf')} disabled={!!busy} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="rebill-statement-pdf-btn">
            {busy === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />} Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Single ledger row (line view + non-invoice single groups)
// ------------------------------------------------------------------
function SingleLedgerRow({ row, basis, onOpen, projectRefLabel }) {
  return (
    <button
      className={`w-full grid grid-cols-[110px_170px_90px_100px_1fr_120px_110px_100px_40px] gap-2 px-3 py-2 items-center border-b border-white/5 hover:bg-white/5 text-left text-sm ${row.status === 'void' ? 'opacity-60' : ''}`}
      onClick={onOpen}
      data-testid={`ledger-row-${row.id}`}
    >
      <div className="text-gray-300 text-xs">{fmtUKDate(row.created_at)}</div>
      <div className="truncate">
        <div className="text-white truncate">{projectRefLabel(row)}</div>
        {row.project_name && <div className="text-gray-500 text-[10px] truncate">{row.project_name}</div>}
      </div>
      <div className="text-gray-300 text-xs">{SOURCE_LABELS[row.source] || row.source}</div>
      <div className="text-gray-200 text-xs capitalize">{row.category}</div>
      <div className="min-w-0">
        <div className="text-white truncate">{row.supplier_name || <span className="text-gray-500">No supplier</span>}{row.description && <span className="text-gray-400 text-xs ml-1.5">· {row.description}</span>}</div>
        <div className="text-gray-400 text-[11px] truncate">
          {row.reference || <span className="italic text-gray-600">No ref</span>}
          <CoverBadge row={row} />
          <RebillBadge row={row} />
          <QueryFlagBadge open={row.inbox_query_open} id={row.id} />
          {row.tags && row.tags.length > 0 && (
            <span className="ml-2">
              {row.tags.slice(0, 3).map(t => (
                <span key={t} className="inline-block bg-white/5 rounded px-1 mr-1">{t}</span>
              ))}
              {row.tags.length > 3 && <span>+{row.tags.length - 3}</span>}
            </span>
          )}
        </div>
      </div>
      <div className="text-right">
        {row.value_pending ? (
          <span className="text-amber-300 text-xs font-medium" data-testid="value-pending-marker">Pending</span>
        ) : (
          <>
            <span className={`font-medium ${captureCoverState(row) === 'covered' ? 'text-gray-500 line-through decoration-emerald-500/60' : 'text-white'}`} data-testid={`ledger-row-value-${row.id}`}>{fmtGBP(rowPence(row, basis))}</span>
            {(row.vat_pence || 0) > 0 && (
              <span className="block text-[10px] text-gray-500 tabular-nums" data-testid={`ledger-row-vat-${row.id}`}>
                {basis === 'net' ? `+ ${fmtGBP(row.vat_pence)} VAT` : `${fmtGBP(row.net_pence)} ex VAT`}
              </span>
            )}
          </>
        )}
      </div>
      <div>
        <Badge variant="outline" className={`${STATUS_COLORS[row.status] || ''} text-[10px] capitalize`}>
          {row.status}
        </Badge>
      </div>
      <div className="text-gray-400 text-xs truncate">{row.captured_by_name}</div>
      <div className="text-right"><ChevronRight className="w-4 h-4 text-gray-500 ml-auto" /></div>
    </button>
  );
}

// ------------------------------------------------------------------
// Invoice group row (invoice view) — one supplier invoice + its lines
// ------------------------------------------------------------------
function InvoiceGroupRow({ group, basis, expanded, onToggle, onOpen, projectRefLabel, selectMode = false, selected = false, onToggleSelect }) {
  const g = group;
  const voided = g.status === 'void';
  return (
    <div className="border-b border-white/5" data-testid={`ledger-invoice-${g.inbox_item_id}`}>
      <div
        role="button" tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
        className={`w-full grid grid-cols-[110px_170px_90px_100px_1fr_120px_110px_100px_40px] gap-2 px-3 py-2 items-center hover:bg-white/5 text-left text-sm cursor-pointer ${voided ? 'opacity-60' : ''} ${selected ? 'bg-sky-500/10' : ''}`}
        data-testid={`ledger-invoice-row-${g.inbox_item_id}`}
      >
        <div className="text-gray-300 text-xs flex items-center gap-2">
          {selectMode && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              className="accent-sky-400 w-4 h-4 shrink-0"
              data-testid={`xero-select-${g.inbox_item_id}`}
            />
          )}
          <span>{fmtUKDate(g.invoice_date || g.created_at)}</span>
        </div>
        <div className="truncate">
          <div className="text-white truncate">{projectRefLabel(g)}</div>
          {g.project_name && <div className="text-gray-500 text-[10px] truncate">{g.project_name}</div>}
        </div>
        <div className="text-gray-300 text-xs inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-[#F5A623]" /> Invoice</div>
        <div className="text-gray-200 text-xs capitalize">{g.category === 'mixed' ? <span className="text-gray-400">Mixed</span> : g.category}</div>
        <div className="min-w-0">
          <div className="text-white truncate">
            {g.supplier_name || <span className="text-gray-500">No supplier</span>}
            <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] text-gray-400"><Files className="w-3 h-3" /> {g.line_count} line{g.line_count === 1 ? '' : 's'}</span>
          </div>
          <div className="text-gray-400 text-[11px] truncate">
            {g.reference || <span className="italic text-gray-600">No ref</span>}
            {g.reconciled === false && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" title="The invoice lines don't add up to the invoice net total" data-testid={`invoice-flag-${g.inbox_item_id}`}>
                <AlertTriangle className="w-3 h-3" /> doesn't reconcile
              </span>
            )}
            <RebillBadge row={{ id: g.inbox_item_id, rebill: g.rebill, rebilled: !g.rebill_pending, billed_to_company: g.billed_to_company, company: g.company }} />
            <XeroBadge status={g.inbox_xero_status} id={g.inbox_item_id} />
            <QueryFlagBadge open={g.inbox_query_open} id={g.inbox_item_id} />
          </div>
        </div>
        <div className="text-right">
          {g.value_pending ? (
            <span className="text-amber-300 text-xs font-medium">Pending</span>
          ) : (
            <>
              <span className="font-medium text-white" data-testid={`invoice-value-${g.inbox_item_id}`}>{fmtGBP(rowPence(g, basis))}</span>
              {(g.vat_pence || 0) > 0 && (
                <span className="block text-[10px] text-gray-500 tabular-nums">
                  {basis === 'net' ? `+ ${fmtGBP(g.vat_pence)} VAT` : `${fmtGBP(g.net_pence)} ex VAT`}
                </span>
              )}
            </>
          )}
        </div>
        <div>
          <Badge variant="outline" className={`${STATUS_COLORS[g.status] || ''} text-[10px] capitalize`}>{g.status}</Badge>
        </div>
        <div className="text-gray-400 text-xs truncate">{g.captured_by_name}</div>
        <div className="text-right">
          <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} className="p-1 rounded hover:bg-white/10 text-gray-400" title={expanded ? 'Hide lines' : 'Show lines'} data-testid={`invoice-expand-${g.inbox_item_id}`}>
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="bg-black/20" data-testid={`invoice-lines-${g.inbox_item_id}`}>
          {(g.lines || []).map(line => (
            <button
              key={line.id}
              onClick={onOpen}
              className={`w-full grid grid-cols-[110px_170px_90px_100px_1fr_120px_110px_100px_40px] gap-2 px-3 py-1.5 items-center border-t border-white/5 hover:bg-white/5 text-left text-xs ${line.status === 'void' ? 'opacity-60' : ''}`}
              data-testid={`invoice-line-${line.id}`}
            >
              <div />
              <div className="truncate text-gray-400 text-[11px] pl-3">{line.project_ref || '—'}</div>
              <div className="text-gray-500 text-[10px] uppercase tracking-wider">Line</div>
              <div className="text-gray-300 capitalize">{line.category}</div>
              <div className="min-w-0 text-gray-300 truncate">{line.description || <span className="text-gray-600 italic">No description</span>}</div>
              <div className="text-right text-gray-200">
                {fmtGBP(rowPence(line, basis))}
                {(line.vat_pence || 0) > 0 && <span className="block text-[10px] text-gray-500">{basis === 'net' ? `+ ${fmtGBP(line.vat_pence)} VAT` : `${fmtGBP(line.net_pence)} ex VAT`}</span>}
              </div>
              <div>{line.status === 'void' && <span className="text-[10px] text-gray-500 line-through">void</span>}</div>
              <div />
              <div className="text-right"><ChevronRight className="w-3.5 h-3.5 text-gray-600 ml-auto" /></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Slideover: invoice-level detail — all lines + source document
// ------------------------------------------------------------------
function InvoiceDetail({ api, isAdmin, inboxItemId, projectId, projects = [], onClose, onChanged }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [basis] = useVatBasis();
  const [showDoc, setShowDoc] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [voidRow, setVoidRow] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkLines, setBulkLines] = useState([]);
  const [bulkReason, setBulkReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const q = projectId ? `?project_id=${projectId}` : '';
      const r = await api.get(`/cost-ledger/invoices/${inboxItemId}${q}`);
      setState({ loading: false, ...r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load invoice');
      onClose();
    }
  }, [api, inboxItemId, projectId, onClose]);
  useEffect(() => { load(); }, [load]);

  const inv = state.invoice;
  const lines = state.lines || [];
  const changed = () => { load(); onChanged?.(); };
  const [rebilling, setRebilling] = useState(false);
  const markInvoiceRebilled = async (rebilled) => {
    setRebilling(true);
    try {
      const r = await api.post('/cost-ledger/rebill/mark', { inbox_item_id: inboxItemId, rebilled });
      toast.success(rebilled ? `Marked ${r.data?.updated || ''} line(s) rebilled` : 'Rebill re-opened');
      changed();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setRebilling(false); }
  };
  const [sendingXero, setSendingXero] = useState(false);
  const retryXero = async () => {
    setSendingXero(true);
    try {
      const r = await api.post(`/cost-inbox/${inboxItemId}/send-xero`);
      if (r.data?.already_sent) toast.info('Already sent to Xero — not sent again');
      else if (r.data?.xero?.status === 'sent') toast.success('Invoice sent to Xero');
      else toast.error(r.data?.xero?.failure_reason || 'Not sent to Xero');
      changed();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSendingXero(false); }
  };
  const markXero = async (status) => {
    setSendingXero(true);
    try {
      await api.post(`/cost-inbox/${inboxItemId}/xero-status`, { status });
      toast.success(status === 'sent' ? 'Marked as sent to Xero' : 'Xero flag cleared — marked not sent');
      changed();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setSendingXero(false); }
  };
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const rebillNotes = state.rebill_notes || [];
  const addRebillNote = async () => {
    const text = noteText.trim();
    if (text.length < 2) return;
    setAddingNote(true);
    try {
      await api.post('/cost-ledger/rebill/note', { inbox_item_id: inboxItemId, text });
      setNoteText('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add note'); }
    finally { setAddingNote(false); }
  };

  const targetNet = inv ? (inv.invoice_net_pence ?? inv.net_pence ?? 0) : 0;
  const targetVat = inv ? (inv.invoice_vat_pence ?? inv.vat_pence ?? 0) : 0;
  // Re-split rewrites the whole invoice, so only offer it on the full (unscoped) invoice.
  const canResplit = isAdmin && !projectId && inv && inv.status !== 'void';
  const startBulk = () => {
    setBulkLines(lines.filter(l => l.status !== 'void').map(l => ({
      id: l.id, project_id: l.project_id || '', category: l.category || '',
      description: l.description || '', amount: (rowPence(l, 'net') / 100).toFixed(2), suggested: false, source: 'user',
    })));
    setBulkReason(''); setBulkMode(true);
  };
  const bulkTotal = bulkLines.reduce((s, l) => s + toPence(l.amount), 0);
  const bulkValid = bulkLines.length > 0 && bulkLines.every(l => l.project_id && l.category && toPence(l.amount) > 0)
    && bulkTotal === targetNet && bulkReason.trim().length >= 5;
  const saveBulk = async () => {
    setSaving(true);
    try {
      const payload = bulkLines.map(l => ({
        id: String(l.id).startsWith('tmp-') ? undefined : l.id,
        project_id: l.project_id || null, category: l.category || null,
        description: l.description || null, net_pounds: toPence(l.amount) / 100,
      }));
      const r = await api.post(`/cost-ledger/invoices/${inboxItemId}/resplit`, { lines: payload, reason: bulkReason.trim() });
      const n = r.data?.line_count || payload.length;
      toast.success(`Re-split into ${n} line${n === 1 ? '' : 's'}${r.data?.removed ? `, ${r.data.removed} removed` : ''}`);
      setBulkMode(false); setBulkReason('');
      changed();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-split failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="ledger-invoice-slideover">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-lg bg-[#1E2530] border-l border-white/10 overflow-y-auto">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-gray-400">Supplier invoice</div>
            <div className="text-lg font-semibold text-white truncate">{inv ? (inv.reference || inv.supplier_name || 'Invoice') : '…'}</div>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && !projectId && inv && inv.has_file !== undefined && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/cost-inbox?open=${inboxItemId}&edit=1`)} className="h-8 border-[#F5A623]/40 text-[#F5A623] bg-[#F5A623]/10 hover:bg-[#F5A623]/20" title="Open in the Cost Inbox to edit the header, Billed to, CIS or lines" data-testid="invoice-detail-edit-inbox">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit in Cost Inbox
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-400 hover:text-white" data-testid="invoice-detail-close"><X className="w-4 h-4" /></Button>
          </div>
        </div>
        {state.loading ? (
          <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : !inv ? null : (
          <div className="p-4 space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2 text-white">
              <Field label="Supplier">{inv.supplier_name || '-'}{inv.supplier_account && <span className="text-[#F5A623] font-mono text-xs ml-1.5">acc {inv.supplier_account}</span>}</Field>
              <Field label="Reference">{inv.reference || '-'}</Field>
              <Field label="Invoice date">{inv.invoice_date ? fmtUKDate(inv.invoice_date) : '-'}</Field>
              <Field label="Project">{inv.project_refs && inv.project_refs.length ? inv.project_refs.join(', ') : 'Overhead'}</Field>
              <Field label="Status"><Badge variant="outline" className={`${STATUS_COLORS[inv.status]} capitalize`}>{inv.status}</Badge></Field>
              <Field label="Lines">{inv.line_count}</Field>
              {inv.billed_to_company && <Field label="Billed to"><span className="text-white">{COMPANY_FULL[inv.billed_to_company] || inv.billed_to_company}</span>{inv.rebill_line_count > 0 && <span className="text-purple-300 text-xs ml-1.5">· {inv.rebill_line_count} to rebill</span>}</Field>}
              <Field label="Net · ex VAT"><span className="font-semibold" data-testid="invoice-detail-net">{fmtGBP(inv.net_pence)}</span></Field>
              <Field label="VAT">{fmtGBP(inv.vat_pence)}</Field>
              <Field label="Gross · inc VAT"><span className="font-semibold" data-testid="invoice-detail-gross">{fmtGBP(inv.value_pence)}</span></Field>
              {inv.cis?.enabled && (
                <Field label="CIS">
                  <span className="text-[#F5A623]" data-testid="invoice-detail-cis">
                    {Number(inv.cis.rate) === 0 ? 'Gross status (0%)' : `${Number(inv.cis.rate)}% deducted`}
                    {inv.cis.workings && <span className="text-gray-300"> · {fmtGBP(inv.cis.workings.deduction_pence)} withheld → pay {fmtGBP(inv.cis.workings.payable_pence)}</span>}
                    <span className="text-[10px] text-gray-500 ml-1">· {inv.cis.vat_treatment === 'reverse_charge' ? 'reverse charge 0%' : 'no VAT'}</span>
                  </span>
                </Field>
              )}
              {inv.livecosts_status && <Field label="LiveCosts"><span className="capitalize">{inv.livecosts_status}</span></Field>}
              <Field label="Xero">
                {(() => { const xs = inv.xero_status || 'not_sent'; return (
                  <span className={`inline-flex items-center gap-1 ${xs === 'sent' ? 'text-emerald-300' : xs === 'pending' ? 'text-gray-400' : 'text-amber-300'}`} data-testid="invoice-detail-xero-status">
                    {xs === 'sent' ? <CheckCircle2 className="w-3.5 h-3.5" /> : xs === 'pending' ? <Clock className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    {xs === 'sent' ? 'Sent' : xs === 'pending' ? 'Sending…' : 'Not sent'}
                    {inv.xero_manual ? <span className="text-[10px] text-gray-500 ml-1">· manual</span> : null}
                  </span>
                ); })()}
              </Field>
            </div>

            {inv.reconciled === false && (
              <div className="rounded p-3 text-sm border bg-amber-500/10 border-amber-500/30 text-amber-200" data-testid="invoice-detail-reconcile-flag">
                <div className="font-medium flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Lines don't reconcile to the invoice</div>
                <div className="text-xs mt-1">The lines add up to {fmtGBP(inv.net_pence)} but the invoice net is {fmtGBP(inv.invoice_net_pence)}. Check the split.</div>
                {canResplit && !bulkMode && (
                  <button type="button" onClick={startBulk} className="mt-2 inline-flex items-center gap-1 text-xs text-amber-100 hover:underline font-medium" data-testid="invoice-reconcile-resplit-btn"><Layers className="w-3.5 h-3.5" /> Re-split to fix</button>
                )}
              </div>
            )}

            {inv.rebill_line_count > 0 && (
              <div className={`rounded p-3 text-sm border ${inv.rebill_pending_count > 0 ? 'bg-purple-500/10 border-purple-500/30 text-purple-200' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'}`} data-testid="invoice-detail-rebill-block">
                <div className="font-medium flex items-center gap-1.5"><ArrowLeftRight className="w-4 h-4" /> {inv.rebill_pending_count > 0 ? `${inv.rebill_line_count} line${inv.rebill_line_count === 1 ? '' : 's'} to rebill` : 'Rebilled ✓'}</div>
                <div className="text-xs mt-1">Billed to {COMPANY_FULL[inv.billed_to_company] || inv.billed_to_company} — {inv.rebill_line_count} line{inv.rebill_line_count === 1 ? '' : 's'} on the other company's jobs. The whole invoice went to {COMPANY_FULL[inv.billed_to_company] || inv.billed_to_company}'s Xero.</div>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => markInvoiceRebilled(inv.rebill_pending_count > 0)} disabled={rebilling} className="mt-2 h-8 border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="invoice-detail-rebill-btn">
                    {rebilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : inv.rebill_pending_count > 0 ? 'Mark rebilled' : 'Undo — still to rebill'}
                  </Button>
                )}
                <div className="mt-3 border-t border-white/10 pt-2" data-testid="rebill-notes-block">
                  <div className="text-[11px] uppercase tracking-wider text-purple-200/70 mb-1.5">Review notes</div>
                  {rebillNotes.length === 0 ? (
                    <div className="text-[11px] text-gray-400 mb-1.5">No notes yet — record what you've queried or agreed. Notes never change the ledger.</div>
                  ) : (
                    <div className="space-y-1.5 mb-2" data-testid="rebill-notes-list">
                      {rebillNotes.map(n => (
                        <div key={n.id} className="text-[11px] text-gray-200 bg-black/20 rounded px-2 py-1" data-testid={`rebill-note-${n.id}`}>
                          <div>{n.text}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5">{n.by_name || 'admin'} · {n.at ? new Date(n.at).toLocaleString('en-GB') : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {isAdmin && (
                    <div className="flex gap-2">
                      <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a review note…" className="h-8 bg-[#1E2530] border-white/20 text-white text-xs" data-testid="rebill-note-input" />
                      <Button size="sm" onClick={addRebillNote} disabled={addingNote || noteText.trim().length < 2} className="h-8 bg-purple-500 hover:bg-purple-400 text-white shrink-0" data-testid="rebill-note-add">
                        {addingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isAdmin && inv.xero_status !== 'pending' && (() => {
              const sent = (inv.xero_status || 'not_sent') === 'sent';
              return (
                <div className={`rounded p-3 text-sm border ${sent ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-amber-500/10 border-amber-500/30 text-amber-200'}`} data-testid="invoice-detail-xero-block">
                  <div className="font-medium flex items-center gap-1.5">
                    {sent ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    {sent ? 'Sent to Xero' : 'Not sent to Xero'}
                    {inv.xero_manual ? <span className="text-[10px] text-gray-400 ml-1 font-normal">· set manually</span> : null}
                  </div>
                  <div className="text-xs mt-1 text-gray-300/90">
                    {sent
                      ? 'Flagged as on Xero. If it was marked by mistake or isn\u2019t really in Xero, clear the flag.'
                      : 'Automatic email-to-bills is still being set up. If you added this invoice to Xero yourself (or it was already there), mark it as sent to clear the \u201cnot to Xero\u201d flag.'}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    {sent ? (
                      <Button size="sm" variant="outline" onClick={() => markXero('not_sent')} disabled={sendingXero} className="h-8 border-amber-500/40 text-amber-100 bg-amber-500/10 hover:bg-amber-500/20" data-testid="invoice-detail-xero-mark-not-sent">
                        {sendingXero ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />} Remove Xero flag
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" onClick={() => markXero('sent')} disabled={sendingXero} className="h-8 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold" data-testid="invoice-detail-xero-mark-sent">
                          {sendingXero ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />} Mark as sent to Xero
                        </Button>
                        <Button size="sm" variant="outline" onClick={retryXero} disabled={sendingXero} className="h-8 border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="invoice-detail-xero-retry">
                          {sendingXero ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Try email send
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="border-t border-white/10 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-gray-400">Lines</div>          {canResplit && !bulkMode && (
                  <button type="button" onClick={startBulk} className="inline-flex items-center gap-1 text-xs text-[#F5A623] hover:underline" data-testid="invoice-resplit-btn"><Layers className="w-3.5 h-3.5" /> Re-split lines</button>
                )}
              </div>
              {bulkMode ? (
                <div className="space-y-3" data-testid="invoice-resplit-editor">
                  <p className="text-[11px] text-gray-400">Re-distribute the invoice across jobs and categories — the lines (ex VAT) must add up to the invoice net {fmtGBP(targetNet)}{targetVat > 0 ? `; VAT ${fmtGBP(targetVat)} is re-split pro-rata` : ''}.</p>
                  <LineItemsTable lines={bulkLines} setLines={setBulkLines} projects={projects} invoiceTotalPence={targetNet} grossPence={targetNet + targetVat} vatPence={targetVat} readOnly={false} />
                  <div>
                    <label className="text-xs text-gray-400">Reason for change (required, min 5 chars)</label>
                    <Textarea value={bulkReason} onChange={e => setBulkReason(e.target.value)} className="bg-[#1E2530] border-white/20 text-white min-h-[64px] mt-1" data-testid="invoice-resplit-reason" placeholder="e.g. moved the fuel line onto the right job" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" onClick={() => setBulkMode(false)} disabled={saving} data-testid="invoice-resplit-cancel">Cancel</Button>
                    <Button onClick={saveBulk} disabled={saving || !bulkValid} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" title={bulkValid ? '' : 'Every line needs a project, category and amount, the lines (ex VAT) must add up to the invoice net, and a reason is required'} data-testid="invoice-resplit-save">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save re-split
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="invoice-detail-lines">
                  {lines.map(line => (
                    <div key={line.id} className={`rounded bg-white/5 p-2.5 ${line.status === 'void' ? 'opacity-60' : ''}`} data-testid={`invoice-detail-line-${line.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-white text-sm truncate"><span className="capitalize">{line.category}</span>{line.description ? ` · ${line.description}` : ''}</div>
                          <div className="text-gray-400 text-[11px]">{line.project_ref || 'Overhead'} · <span className="capitalize">{line.status}</span></div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-white text-sm tabular-nums">{fmtGBP(rowPence(line, basis))}</div>
                          {(line.vat_pence || 0) > 0 && <div className="text-[10px] text-gray-500 tabular-nums">{basis === 'net' ? `+ ${fmtGBP(line.vat_pence)} VAT` : `${fmtGBP(line.net_pence)} ex VAT`}</div>}
                        </div>
                      </div>
                      {isAdmin && line.status !== 'void' && (
                        <div className="flex gap-3 mt-2">
                          <button type="button" onClick={() => setEditRow(line)} className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white" data-testid={`invoice-line-edit-${line.id}`}><Pencil className="w-3 h-3" /> Edit</button>
                          <button type="button" onClick={() => setVoidRow(line)} className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200" data-testid={`invoice-line-void-${line.id}`}><BanIcon className="w-3 h-3" /> Void</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {inv.has_file ? (
              <div className="border-t border-white/10 pt-3">
                <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Source invoice</div>
                <button type="button" onClick={() => setShowDoc(true)} className="block w-40 rounded-md overflow-hidden border border-white/10 hover:border-[#F5A623]/60 transition-colors" data-testid="invoice-detail-thumb-btn">
                  <AuthThumb endpoint={`/cost-ledger/entries/${inv.document_row_id}/document/pages/0?width=400`} testKey={`inv-doc-${inv.inbox_item_id}`} size="lg" fit="contain" className="w-full h-52" />
                </button>
                <div className="text-[11px] text-gray-500 mt-1">{inv.original_filename}{inv.page_count > 1 ? ` · ${inv.page_count} pages` : ''} · tap to view full size</div>
              </div>
            ) : (
              <div className="border-t border-white/10 pt-3 text-xs text-gray-500" data-testid="invoice-detail-no-file">No source document attached to this invoice.</div>
            )}

            <div className="border-t border-white/10 pt-3">
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Audit history</div>
              {(!state.audit_history || state.audit_history.length === 0) ? (
                <div className="text-xs text-gray-500">No corrections recorded.</div>
              ) : (
                <div className="space-y-2" data-testid="invoice-detail-audit">
                  {state.audit_history.map(a => (
                    <div key={a.id} className="text-xs bg-white/5 rounded p-2">
                      <div className="text-gray-400"><span className="capitalize text-[#F5A623]">{(a.action || '').replace(/_/g, ' ')}</span> · {fmtUKDateTime(a.created_at)} · {a.performed_by_name}</div>
                      {a.reason && <div className="text-white mt-1">"{a.reason}"</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {showDoc && inv && (
          <InvoiceDocViewer
            row={{ id: inv.document_row_id, supplier_name: inv.supplier_name, inbox_page_count: inv.page_count }}
            inboxItem={{ page_count: inv.page_count, original_filename: inv.original_filename, supplier_name: inv.supplier_name, reference: inv.reference, livecosts: { status: inv.livecosts_status } }}
            onClose={() => setShowDoc(false)}
          />
        )}
        {editRow && <EditDialog api={api} row={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); changed(); }} />}
        {voidRow && <VoidDialog api={api} rowId={voidRow.id} onClose={() => setVoidRow(null)} onVoided={() => { setVoidRow(null); changed(); }} />}
      </div>
    </div>
  );
}


// ------------------------------------------------------------------
// Retry button (write-failure row)
// ------------------------------------------------------------------
function RetryButton({ api, captureId, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const click = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      await api.post(`/cost-ledger/reconciliation/retry-write-failure/${captureId}`);
      toast.success('Ledger row created');
      onSuccess?.();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Retry failed');
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" onClick={click} disabled={busy}
            className="bg-white/10 hover:bg-white/20 text-white text-xs h-7"
            data-testid={`writefailure-retry-${captureId}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" /> Retry</>}
    </Button>
  );
}

// ------------------------------------------------------------------
// Slideover: row detail + admin actions
// ------------------------------------------------------------------
function LedgerRowDetail({ api, isAdmin, rowId, onClose, onChanged }) {
  const [state, setState] = useState({ loading: true });
  const [voidOpen, setVoidOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [basis] = useVatBasis();

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const r = await api.get(`/cost-ledger/entries/${rowId}`);
      setState({ loading: false, ...r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load row');
      onClose();
    }
  }, [api, rowId, onClose]);
  useEffect(() => { load(); }, [load]);

  const row = state.row;
  const isVoided = row?.status === 'void';
  const coverState = captureCoverState(row);
  const [covering, setCovering] = useState(false);
  const toggleCover = async () => {
    setCovering(true);
    try {
      await api.post(`/cost-ledger/entries/${row.id}/cover`, { covered: coverState !== 'covered' });
      toast.success(coverState === 'covered' ? 'Counts again until the invoice lands' : 'Marked superseded — the invoice line is the cost now');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setCovering(false); }
  };
  const [rebilling, setRebilling] = useState(false);
  const toggleRebilled = async () => {
    setRebilling(true);
    try {
      await api.post('/cost-ledger/rebill/mark', { row_ids: [row.id], rebilled: !row.rebilled });
      toast.success(row.rebilled ? 'Rebill re-opened' : 'Marked rebilled');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setRebilling(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="ledger-detail-slideover">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-lg bg-[#1E2530] border-l border-white/10 overflow-y-auto">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400">Ledger row</div>
            <div className="text-lg font-semibold text-white">
              {row ? (row.reference || `${row.category} · ${fmtUKDate(row.created_at)}`) : '…'}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}
                  className="text-gray-400 hover:text-white" data-testid="ledger-detail-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {state.loading ? (
          <div className="p-8 text-center text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="p-4 space-y-4 text-sm">
            {/* Core fields */}
            <div className="grid grid-cols-2 gap-2 text-white">
              <Field label={`Value · ${BASIS_LABEL[basis]}`}>
                {row.value_pending
                  ? <span className="text-amber-300">Pending — awaiting invoice</span>
                  : <span className="font-semibold" data-testid="ledger-detail-value">{fmtGBP(rowPence(row, basis))}</span>}
              </Field>
              <Field label="Status">
                <Badge variant="outline" className={`${STATUS_COLORS[row.status]} capitalize`}>{row.status}</Badge>
              </Field>
              <Field label="Category"><span className="capitalize">{row.category}</span></Field>
              <Field label="Source">{SOURCE_LABELS[row.source] || row.source}</Field>
              <Field label="Project">{row.project_ref || 'Overhead'}</Field>
              <Field label="Company">{row.company || '-'}</Field>
              {row.billed_to_company && <Field label="Billed to">{COMPANY_FULL[row.billed_to_company] || row.billed_to_company}{row.rebill && <span className="text-purple-300 text-xs ml-1.5">· rebill</span>}</Field>}
              <Field label="Supplier">{row.supplier_name || '-'}{row.supplier_account && <span className="text-[#F5A623] font-mono text-xs ml-1.5">acc {row.supplier_account}</span>}</Field>
              <Field label="Reference">{row.reference || '-'}</Field>
              {row.description && <Field label="Description"><span className="text-gray-100">{row.description}</span></Field>}
              {row.net_pence != null && row.net_pence > 0 && <Field label="Ex VAT + VAT = inc VAT"><span data-testid="ledger-detail-vat-split">{fmtGBP(row.net_pence)} <span className="text-gray-400">+ {fmtGBP(row.vat_pence || 0)} ({row.vat_rate ?? 20}%)</span> = {fmtGBP(row.value_pence)}</span></Field>}
              <Field label="Captured by">{row.captured_by_name} ({row.captured_by_role})</Field>
              <Field label="View">{row.view_context}</Field>
            </div>
            {coverState && (
              <div className={`rounded p-3 text-sm border ${coverState === 'covered' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`} data-testid="ledger-detail-cover-block">
                <div className={`font-medium ${coverState === 'covered' ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {coverState === 'covered' ? 'Superseded by a supplier invoice — not counted in the job total' : `Provisional capture${row.details_only ? ' (details only, nothing sent to LiveCosts)' : ''} — counts until the supplier invoice is posted`}
                </div>
                {coverState === 'covered' && row.matched_at && <div className="text-xs text-gray-400 mt-1">Superseded {fmtUKDateTime(row.matched_at)}</div>}
                {coverState === 'awaiting' && captureAgeDays(row) >= CHASE_DAYS && <div className="text-xs text-red-300 mt-1 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Over 60 days ({captureAgeDays(row)}d) with no invoice — chase the supplier.</div>}
                {isAdmin && !isVoided && (
                  <Button size="sm" variant="outline" onClick={toggleCover} disabled={covering} className="mt-2 h-8 border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="ledger-detail-cover-btn">
                    {covering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : coverState === 'covered' ? 'Undo — count it again' : 'Mark superseded by invoice'}
                  </Button>
                )}
              </div>
            )}
            {row.rebill && (
              <div className={`rounded p-3 text-sm border ${row.rebilled ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-purple-500/10 border-purple-500/30'}`} data-testid="ledger-detail-rebill-block">
                <div className={`font-medium ${row.rebilled ? 'text-emerald-300' : 'text-purple-300'}`}>
                  {row.rebilled ? 'Rebilled — recharged to the other company' : `To rebill — ${COMPANY_FULL[row.billed_to_company] || row.billed_to_company} paid this ${COMPANY_FULL[row.company] || row.company} cost`}
                </div>
                <div className="text-xs text-gray-400 mt-1">The invoice was sent to {COMPANY_FULL[row.billed_to_company] || row.billed_to_company}'s Xero; the cost stays on {row.project_ref || 'this project'}.</div>
                {row.rebilled && row.rebilled_at && <div className="text-xs text-gray-400 mt-0.5">Rebilled {fmtUKDateTime(row.rebilled_at)}{row.rebilled_by_name ? ` by ${row.rebilled_by_name}` : ''}</div>}
                {isAdmin && !isVoided && (
                  <Button size="sm" variant="outline" onClick={toggleRebilled} disabled={rebilling} className="mt-2 h-8 border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="ledger-detail-rebill-btn">
                    {rebilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : row.rebilled ? 'Undo — still to rebill' : 'Mark rebilled'}
                  </Button>
                )}
              </div>
            )}
            {row.tags && row.tags.length > 0 && (
              <div>
                <div className="text-xs text-gray-400 mb-1">Tags</div>
                <div className="flex flex-wrap gap-1">
                  {row.tags.map(t => <span key={t} className="text-xs bg-white/10 rounded px-2 py-0.5">{t}</span>)}
                </div>
              </div>
            )}

            {isVoided && (
              <div className="bg-red-500/10 border border-red-500/40 rounded p-3 text-sm" data-testid="voided-detail-block">
                <div className="text-red-300 font-medium">Voided</div>
                <div className="text-red-200/80 text-xs mt-1">
                  {fmtUKDateTime(row.voided_at)} by {row.voided_by_name}
                </div>
                <div className="mt-2 text-white text-sm">{row.voided_reason}</div>
              </div>
            )}

            {row.last_correction_reason && (
              <div className="bg-white/5 rounded p-3 text-xs">
                <div className="text-gray-400">Last correction — {fmtUKDateTime(row.last_corrected_at)} by {row.last_corrected_by_name}</div>
                <div className="text-white mt-1">{row.last_correction_reason}</div>
              </div>
            )}

            {/* Linked capture */}
            {state.capture && (
              <div className="border-t border-white/10 pt-3">
                <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
                  <Camera className="inline w-3 h-3 mr-1" /> Linked capture
                </div>
                <div className="text-sm text-white">{state.capture.linked_project_ref || 'Overhead'} · {state.capture.company}</div>
                <div className="text-xs text-gray-400 mt-1">{fmtUKDateTime(state.capture.created_at)}</div>
                {state.capture.output_pdf?.onedrive_url && (
                  <a href={state.capture.output_pdf.onedrive_url} target="_blank" rel="noreferrer"
                     className="text-[#F5A623] text-xs mt-1 inline-block hover:underline">
                    Open PDF
                  </a>
                )}
              </div>
            )}

            {/* Linked diary entry */}
            {state.diary_entry && (
              <div className="border-t border-white/10 pt-3">
                <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
                  <BookOpen className="inline w-3 h-3 mr-1" /> Linked diary entry
                </div>
                <div className="text-sm text-white">{state.diary_entry.title}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {state.diary_entry.date} · by {state.diary_entry.author_name}
                </div>
                {state.diary_entry.notes && state.diary_entry.notes.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {state.diary_entry.notes.map(n => (
                      <div key={n.id || n.created_at} className="text-xs bg-white/5 rounded p-2">
                        <div className="text-gray-400">{fmtUKDateTime(n.created_at)} · {n.created_by_name}</div>
                        <div className="text-white">{n.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Audit history */}
            <div className="border-t border-white/10 pt-3">
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Audit history</div>
              {state.audit_history.length === 0 ? (
                <div className="text-xs text-gray-500">No corrections recorded.</div>
              ) : (
                <div className="space-y-2" data-testid="ledger-detail-audit-history">
                  {state.audit_history.map(a => (
                    <div key={a.id} className="text-xs bg-white/5 rounded p-2">
                      <div className="text-gray-400">
                        <span className="capitalize text-[#F5A623]">{a.action.replace('_', ' ')}</span> ·
                        {' '}{fmtUKDateTime(a.created_at)} · {a.performed_by_name} ({a.performed_by_role})
                      </div>
                      {a.reason && <div className="text-white mt-1">"{a.reason}"</div>}
                      {a.changed_fields && a.changed_fields.length > 0 && (
                        <div className="text-gray-500 mt-1">Fields: {a.changed_fields.join(', ')}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Admin actions */}
            {isAdmin && !isVoided && (
              <div className="border-t border-white/10 pt-3 flex gap-2 sticky bottom-0 bg-[#1E2530] pb-2">
                <Button onClick={() => setEditOpen(true)} variant="outline"
                        className="flex-1 border-white/20 text-white bg-white/5 hover:bg-white/10"
                        data-testid="ledger-detail-edit-btn">
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </Button>
                <Button onClick={() => setVoidOpen(true)} variant="outline"
                        className="flex-1 border-red-500/40 text-red-300 hover:bg-red-500/10"
                        data-testid="ledger-detail-void-btn">
                  <BanIcon className="w-4 h-4 mr-2" /> Void
                </Button>
              </div>
            )}
          </div>
        )}

        {voidOpen && row && (
          <VoidDialog api={api} rowId={row.id} onClose={() => setVoidOpen(false)} onVoided={() => { setVoidOpen(false); onChanged(); }} />
        )}
        {editOpen && row && (
          <EditDialog api={api} row={row} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); onChanged(); }} />
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

// ------------------------------------------------------------------
// Admin dialogs
// ------------------------------------------------------------------
function VoidDialog({ api, rowId, onClose, onVoided }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const disabled = reason.trim().length < 5 || busy;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/cost-ledger/entries/${rowId}/void`, { reason: reason.trim() });
      toast.success('Row voided');
      onVoided();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#2A3240] border-white/10 text-white" data-testid="void-dialog">
        <DialogHeader><DialogTitle>Void this ledger row?</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-300">
          Voiding removes this row from all totals but keeps it visible in the master ledger with a full audit trail. This cannot be undone.
        </p>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-400">Reason (required, min 5 chars)</label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)}
                    className="bg-[#1E2530] border-white/20 text-white mt-1 min-h-[80px]"
                    data-testid="void-reason-input" placeholder="e.g. duplicate of DN-31228 already captured" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={disabled}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="void-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BanIcon className="w-4 h-4 mr-2" />}
            Void row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ api, row, onClose, onSaved }) {
  const rowNet = row.net_pence ?? row.value_pence;
  const [category,     setCategory]     = useState(row.category);
  const [valuePounds,  setValuePounds]  = useState(row.value_pending ? '' : (rowNet / 100).toFixed(2));   // ex VAT
  const [vatPounds,    setVatPounds]    = useState(row.value_pending ? '' : ((row.vat_pence || 0) / 100).toFixed(2));
  const [valuePending, setValuePending] = useState(!!row.value_pending);
  const [supplierName, setSupplierName] = useState(row.supplier_name || '');
  const [reference,    setReference]    = useState(row.reference || '');
  const [tagsInput,    setTagsInput]    = useState((row.tags || []).join(', '));
  const [reason,       setReason]       = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { reason: reason.trim() };
      if (category !== row.category) body.category = category;
      const newTags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
      if (JSON.stringify(newTags) !== JSON.stringify(row.tags || [])) body.tags = newTags;
      if (supplierName !== (row.supplier_name || '')) body.supplier_name = supplierName || null;
      if (reference    !== (row.reference    || '')) body.reference    = reference    || null;
      if (valuePending !== !!row.value_pending) body.value_pending = valuePending;
      if (!valuePending) {
        const net = parseFloat(valuePounds), vat = parseFloat(vatPounds || '0');
        if (!isNaN(net) && (Math.round(net * 100) !== rowNet || Math.round((isNaN(vat) ? 0 : vat) * 100) !== (row.vat_pence || 0))) {
          body.net_pounds = net;
          body.vat_pounds = isNaN(vat) ? 0 : vat;
        }
      }
      await api.patch(`/cost-ledger/entries/${row.id}`, body);
      toast.success('Row updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-lg" data-testid="edit-dialog">
        <DialogHeader><DialogTitle>Edit ledger row</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="edit-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {CATEGORY_ORDER.map(c => <SelectItem key={c} value={c} className="text-white">{CATEGORY_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-400">Value ex VAT (£)</label>
              <Input type="number" step="0.01" value={valuePounds} disabled={valuePending}
                     onChange={e => setValuePounds(e.target.value)}
                     className="bg-[#1E2530] border-white/20 text-white"
                     data-testid="edit-value-pounds" />
              <label className="text-xs text-gray-400 mt-2 block">VAT (£)</label>
              <Input type="number" step="0.01" value={vatPounds} disabled={valuePending}
                     onChange={e => setVatPounds(e.target.value)}
                     className="bg-[#1E2530] border-white/20 text-white"
                     data-testid="edit-vat-pounds" />
              <label className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                <input type="checkbox" checked={valuePending} onChange={e => setValuePending(e.target.checked)}
                       data-testid="edit-value-pending" className="accent-[#F5A623]" />
                Value pending
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400">Supplier</label>
            <Input value={supplierName} onChange={e => setSupplierName(e.target.value)}
                   className="bg-[#1E2530] border-white/20 text-white" data-testid="edit-supplier" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Reference</label>
            <Input value={reference} onChange={e => setReference(e.target.value)}
                   className="bg-[#1E2530] border-white/20 text-white" data-testid="edit-reference" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Tags (comma-separated)</label>
            <Input value={tagsInput} onChange={e => setTagsInput(e.target.value)}
                   className="bg-[#1E2530] border-white/20 text-white" data-testid="edit-tags" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Reason for change (required, min 5 chars)</label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)}
                      className="bg-[#1E2530] border-white/20 text-white min-h-[70px]"
                      data-testid="edit-reason" placeholder="e.g. supplier corrected after invoice arrived" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 5}
                  className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
                  data-testid="edit-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualEntryDialog({ api, projects, onClose, onCreated }) {
  const [projectId,    setProjectId]    = useState('null');
  const [category,     setCategory]     = useState('material');
  const [valuePounds,  setValuePounds]  = useState('');
  const [vatPounds,    setVatPounds]    = useState('');
  const [valuePending, setValuePending] = useState(false);
  const [supplierName, setSupplierName] = useState('');
  const [reference,    setReference]    = useState('');
  const [company,      setCompany]      = useState('CON');
  const [tagsInput,    setTagsInput]    = useState('');
  const [reason,       setReason]       = useState('');
  const [busy, setBusy] = useState(false);

  const canSubmit = () => {
    if (reason.trim().length < 5) return false;
    if (!valuePending) {
      const p = parseFloat(valuePounds);
      if (isNaN(p) || p <= 0) return reference.trim().length > 0; // pending path
    }
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        reason: reason.trim(),
        category,
        company,
        project_id: projectId === 'null' ? null : projectId,
        supplier_name: supplierName.trim() || null,
        reference:     reference.trim()    || null,
        tags:          tagsInput.split(',').map(t => t.trim()).filter(Boolean),
        value_pending: valuePending,
      };
      const parsed = parseFloat(valuePounds);
      if (!isNaN(parsed) && parsed > 0) body.value_pounds = parsed;
      const vat = parseFloat(vatPounds);
      if (!isNaN(vat) && vat > 0) body.vat_pounds = vat;
      await api.post('/cost-ledger/entries/manual', body);
      toast.success('Manual entry created');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Manual entry failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-lg" data-testid="manual-dialog">
        <DialogHeader><DialogTitle>Add a manual ledger entry</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400">Project</label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="manual-project"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
                  <SelectItem value="null" className="text-white">Overhead (no project)</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id} className="text-white">{p.project_ref}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-400">Company</label>
              <Select value={company} onValueChange={setCompany}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="manual-company"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  <SelectItem value="CON" className="text-white">Lux Construction</SelectItem>
                  <SelectItem value="DEV" className="text-white">Lux Developments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-400">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="manual-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {CATEGORY_ORDER.map(c => <SelectItem key={c} value={c} className="text-white">{CATEGORY_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-400">Value ex VAT (£)</label>
              <Input type="number" step="0.01" value={valuePounds} disabled={valuePending}
                     onChange={e => setValuePounds(e.target.value)}
                     className="bg-[#1E2530] border-white/20 text-white" data-testid="manual-value" />
              <label className="text-xs text-gray-400 mt-2 block">VAT (£) <span className="text-gray-500">· optional</span></label>
              <Input type="number" step="0.01" value={vatPounds} disabled={valuePending}
                     onChange={e => setVatPounds(e.target.value)}
                     className="bg-[#1E2530] border-white/20 text-white" data-testid="manual-vat" />
              <label className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                <input type="checkbox" checked={valuePending} onChange={e => setValuePending(e.target.checked)}
                       data-testid="manual-value-pending" className="accent-[#F5A623]" />
                Value pending
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400">Supplier</label>
            <Input value={supplierName} onChange={e => setSupplierName(e.target.value)}
                   className="bg-[#1E2530] border-white/20 text-white" data-testid="manual-supplier" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Reference (required if value is pending)</label>
            <Input value={reference} onChange={e => setReference(e.target.value)}
                   className="bg-[#1E2530] border-white/20 text-white" data-testid="manual-reference" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Tags (comma-separated)</label>
            <Input value={tagsInput} onChange={e => setTagsInput(e.target.value)}
                   className="bg-[#1E2530] border-white/20 text-white" data-testid="manual-tags" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Reason (required, min 5 chars)</label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)}
                      className="bg-[#1E2530] border-white/20 text-white min-h-[70px]"
                      data-testid="manual-reason" placeholder="e.g. cash purchase, no receipt available" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !canSubmit()}
                  className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
                  data-testid="manual-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Create row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Opening balances dialog (admin only) — L-2 Group 2
// ------------------------------------------------------------------
function OpeningBalancesDialog({ api, projects, onClose, onChanged }) {
  const OB_CATS = ['labour', 'material', 'plant'];
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading]     = useState(false);
  const [balances, setBalances]   = useState([]);
  const [asOf, setAsOf]           = useState('');
  const [reason, setReason]       = useState('');
  const [values, setValues]       = useState({ labour: '', material: '', plant: '' });
  const [busy, setBusy]           = useState(false);
  const [adjust, setAdjust]       = useState(null); // { row_id, category, newVal, reason }

  const loadBalances = useCallback(async (pid) => {
    if (!pid) { setBalances([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/cost-ledger/opening-balances?project_id=${pid}`);
      setBalances(r.data.balances || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load opening balances');
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { setAdjust(null); loadBalances(projectId); }, [projectId, loadBalances]);

  const byCat = Object.fromEntries(balances.map(b => [b.category, b]));
  const existingCats = OB_CATS.filter(c => byCat[c]?.row_id);
  const missingCats  = OB_CATS.filter(c => !byCat[c]?.row_id);

  const canSaveNew = projectId && reason.trim().length >= 5 &&
    missingCats.some(c => { const v = parseFloat(values[c]); return !isNaN(v) && v > 0; });

  const saveNew = async () => {
    setBusy(true);
    try {
      const body = { project_id: projectId, reason: reason.trim(), as_of_date: asOf || null };
      for (const c of missingCats) {
        const v = parseFloat(values[c]);
        if (!isNaN(v) && v > 0) body[`${c}_pounds`] = v;
      }
      await api.post('/cost-ledger/opening-balances', body);
      toast.success('Opening balances saved');
      setValues({ labour: '', material: '', plant: '' }); setReason(''); setAsOf('');
      await loadBalances(projectId);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const submitAdjust = async () => {
    if (!adjust) return;
    setBusy(true);
    try {
      await api.post(`/cost-ledger/opening-balances/${adjust.row_id}/adjust`, {
        new_value_pounds: parseFloat(adjust.newVal),
        reason: adjust.reason.trim(),
      });
      toast.success('Opening balance adjusted');
      setAdjust(null);
      await loadBalances(projectId);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Adjust failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-lg" data-testid="ob-dialog">
        <DialogHeader><DialogTitle>Opening balances</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-400 -mt-2">
          Enter cutover spend-to-date for a project. Stored as confirmed ledger rows and counted in totals immediately.
        </p>
        <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
          <div>
            <label className="text-xs text-gray-400">Project</label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="ob-project-select">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
                {projects.map(p => <SelectItem key={p.id} value={p.id} className="text-white">{p.project_ref}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {projectId && loading && (
            <div className="text-center text-gray-400 py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
          )}

          {projectId && !loading && (
            <>
              {existingCats.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wider text-gray-400">Current opening balances</div>
                  {existingCats.map(c => {
                    const b = byCat[c];
                    return (
                      <div key={c} className="bg-white/5 rounded p-3" data-testid={`ob-current-${c}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs text-gray-400">{CATEGORY_LABELS[c]}</div>
                            <div className="text-lg font-semibold" data-testid={`ob-current-value-${c}`}>{fmtGBP(b.value_pence)}</div>
                            {b.as_of_date && <div className="text-[11px] text-gray-500">as of {b.as_of_date}</div>}
                          </div>
                          {adjust?.category !== c && (
                            <Button size="sm" variant="outline"
                                    className="border-white/20 text-white bg-white/5 hover:bg-white/10"
                                    onClick={() => setAdjust({ row_id: b.row_id, category: c, newVal: (b.value_pence / 100).toFixed(2), reason: '' })}
                                    data-testid={`ob-adjust-${c}-btn`}>
                              <Pencil className="w-3 h-3 mr-1" /> Adjust
                            </Button>
                          )}
                        </div>
                        {adjust?.category === c && (
                          <div className="mt-3 space-y-2 border-t border-white/10 pt-3" data-testid="ob-adjust-form">
                            <div>
                              <label className="text-xs text-gray-400">New value (£)</label>
                              <Input type="number" step="0.01" value={adjust.newVal}
                                     onChange={e => setAdjust(a => ({ ...a, newVal: e.target.value }))}
                                     className="bg-[#1E2530] border-white/20 text-white" data-testid="ob-adjust-value" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-400">Reason (required, min 5 chars)</label>
                              <Textarea value={adjust.reason}
                                        onChange={e => setAdjust(a => ({ ...a, reason: e.target.value }))}
                                        className="bg-[#1E2530] border-white/20 text-white min-h-[60px]"
                                        data-testid="ob-adjust-reason"
                                        placeholder="e.g. corrected after month-end reconciliation" />
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button size="sm" variant="ghost" onClick={() => setAdjust(null)}>Cancel</Button>
                              <Button size="sm"
                                      disabled={busy || isNaN(parseFloat(adjust.newVal)) || parseFloat(adjust.newVal) <= 0 || adjust.reason.trim().length < 5}
                                      onClick={submitAdjust}
                                      className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
                                      data-testid="ob-adjust-submit-btn">
                                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save adjustment
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {missingCats.length > 0 && (
                <div className="space-y-2" data-testid="ob-set-form">
                  <div className="text-xs uppercase tracking-wider text-gray-400">
                    Set opening balance{missingCats.length > 1 ? 's' : ''}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {missingCats.map(c => (
                      <div key={c}>
                        <label className="text-xs text-gray-400">{CATEGORY_LABELS[c]} (£)</label>
                        <Input type="number" step="0.01" value={values[c]}
                               onChange={e => setValues(v => ({ ...v, [c]: e.target.value }))}
                               className="bg-[#1E2530] border-white/20 text-white"
                               data-testid={`ob-input-${c}`} placeholder="0.00" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">As of date (optional)</label>
                    <Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
                           className="bg-[#1E2530] border-white/20 text-white" data-testid="ob-as-of" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Reason (required, min 5 chars)</label>
                    <Textarea value={reason} onChange={e => setReason(e.target.value)}
                              className="bg-[#1E2530] border-white/20 text-white min-h-[60px]"
                              data-testid="ob-reason" placeholder="e.g. spend to date at cutover 01/07/2026" />
                  </div>
                  <div className="flex justify-end">
                    <Button disabled={busy || !canSaveNew} onClick={saveNew}
                            className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
                            data-testid="ob-save-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save opening balances
                    </Button>
                  </div>
                </div>
              )}

              {missingCats.length === 0 && existingCats.length === 3 && (
                <div className="text-xs text-gray-500" data-testid="ob-all-set">
                  All three categories have an opening balance. Use Adjust to change one.
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="ob-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
